import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClientWaitlistsQuery } from '../../services/hooks/useWaitlist';
import { useClientDetailsQuery, useClientEventsQuery } from '../../services/hooks/useClientes';
import { useCancelarPorClienteMutation } from '../../services/hooks/useAsistencia';
import { Loader2, ArrowLeft, ChevronDown, ChevronUp, Calendar, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Plaza, Suscripcion } from '../../types/index';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import parse from 'html-react-parser';
import { calcularEdad, esMenorDeEdad, formatearFecha } from '../../utils/dateHelpers';
import EditarDatosPersonales from './EditarDatosPersonales';
import EditarSuscripcion from './EditarSuscripcion';
import PagadorSuscripcionInfo from './PagadorSuscripcionInfo';
import BeneficiarioInfo from './BeneficiarioInfo';
import GestionarPlazasCliente from '../plazas/GestionarPlazasCliente';
import PagosClienteSection from './PagosClienteSection';
import PortalAccountStatus from './PortalAccountStatus';
import ClientActivitySection from './ClientActivitySection';
import ClientRecoveriesCard from './ClientRecoveriesCard';
import ClientAttendanceCard from './ClientAttendanceCard';
import { useDeleteClientSubscription } from '../../services/hooks/useClienteSuscripciones';
import { useSafeMounted } from '../../services/hooks/useSafeMounted';
import { usePageNavigation } from '../../services/hooks';
import { formatPendingMonths } from '../../utils/utils';
import { fetchPagosBySuscripcion } from '../../services/api/pagoService';
import { ContractsComingSoon } from '../contratos';
import { useActiveContractQuery } from '../../services/hooks/useContratos';
import { toast } from 'react-toastify';
import { etiquetaSuscripcion } from '../../utils/suscripciones';
import { DesglosePrecio } from '../suscripciones/PrecioSuscripcion';

const ClienteDetalle = () => {
    usePageNavigation();
    const isMounted = useSafeMounted();

    const { clientId, tenantCode } = useParams<{ clientId: string; tenantCode: string }>();
    // D1 — clases que este cliente está esperando. Solo lectura: sirve para
    // responder en mostrador sin tener que entrar en su portal.
    const { data: enEspera = [] } = useClientWaitlistsQuery(clientId);
    const { data: client, isLoading, error } = useClientDetailsQuery(clientId);
    
    // Obtener contrato activo del cliente
    const { data: activeContract } = useActiveContractQuery(clientId || '');
    
    // Verificar si el cliente tiene suscripciones de "pilates-otros-pagos"
    const suscripcionOtrosPagos = client?.suscripciones?.find((s: Suscripcion) => s.nombreSuscripcion === "pilates-otros-pagos");
    
    const [showEvents, setShowEvents] = useState(false);
    const [eventLimit, setEventLimit] = useState(5);
    
    // Estados para controlar los modales
    const [showEditarDatos, setShowEditarDatos] = useState(false);
    const [suscripcionEnEdicion, setSuscripcionEnEdicion] = useState<string | null>(null);
    const [showGestionarPlazas, setShowGestionarPlazas] = useState(false);
    
    // Nuevo estado para el modal de confirmación
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");

    // Hook para eliminar cliente
    const deleteClientMutation = useDeleteClientSubscription();

    // "No viene a esta clase": el centro cancela en nombre del cliente.
    const [eventoACancelar, setEventoACancelar] = useState<string | null>(null);
    const { mutateAsync: cancelarPorCliente, isPending: cancelandoPorCliente } = useCancelarPorClienteMutation();

    // Cargar eventos solo cuando se expande la sección
    const { 
        data: eventos, 
        isLoading: eventsLoading 
    } = useClientEventsQuery(clientId, {
        limit: eventLimit,
        enabled: showEvents // Solo hacer la consulta cuando se muestra la sección
    });

    // Función para ordenar las plazas por día de la semana
    const ordenarPorDiaSemana = (plazas: Plaza[]) => {
        const ordenDias: { [key: string]: number } = {
            'Lunes': 1,
            'Martes': 2,
            'Miércoles': 3, 
            'Jueves': 4,
            'Viernes': 5,
            'Sábado': 6,
            'Domingo': 7
        };
        
        return [...plazas].sort((a, b) => {
            // Primero ordenar por día de la semana
            const ordenDiaA = ordenDias[a.dia] || 99;  // Si no encuentra el día, lo pone al final
            const ordenDiaB = ordenDias[b.dia] || 99;
            
            if (ordenDiaA !== ordenDiaB) {
                return ordenDiaA - ordenDiaB;
            }
            
            // Si son el mismo día, ordenar por hora
            const horaA = a.horaInicio || '';
            const horaB = b.horaInicio || '';
            return horaA.localeCompare(horaB);
        });
    };
    

    //Funcion que formatea los montos con separadores de miles y decimales
    const formatNumber = (num: number | null | undefined): string => {
        const safeNum = num ?? 0; // Si num es null o undefined, usar 0
        const parts = safeNum.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return parts.join(',');
    };


    // Función para verificar si es cliente de otros pagos y tiene pagos abiertos
    const verificarPagosAbiertos = async () => {
        const esClienteOtrosPagos = client.suscripciones?.some((s: Suscripcion) => 
            s.nombreSuscripcion === "pilates-otros-pagos"
        );
        
        if (!esClienteOtrosPagos || !suscripcionOtrosPagos) return { 
            tienesPagosAbiertos: false, 
            cantidadPagosAbiertos: 0,
            pagosAbiertos: []
        };
        
        // Obtener pagos desde el backend.
        //
        // Antes esto usaba `fetch` contra `/payments/suscripcion/:id`, y fallaba
        // por partida doble: sin el interceptor no llevaba ni el prefijo de
        // tenant ni el token, y esa ruta no existe — el backend expone
        // `/payments/:suscripcion_id`. El 404 lo absorbía el catch de abajo, así
        // que el aviso de pagos pendientes no se mostraba nunca aunque los
        // hubiera, y se borraban igual al confirmar la baja.
        try {
            const pagos = await fetchPagosBySuscripcion(suscripcionOtrosPagos._id);
            const pagosAbiertos = pagos.filter((pago) => pago.estado === 'Abierto');
            
            return { 
                tienesPagosAbiertos: pagosAbiertos.length > 0, 
                cantidadPagosAbiertos: pagosAbiertos.length,
                pagosAbiertos
            };
        } catch (error) {
            console.error('Error al verificar pagos:', error);
            return { 
                tienesPagosAbiertos: false, 
                cantidadPagosAbiertos: 0,
                pagosAbiertos: []
            };
        }
    };

    // Función para manejar el clic del botón "Dar de baja"
    const handleDeleteButtonClick = async () => {
        const { tienesPagosAbiertos, cantidadPagosAbiertos, pagosAbiertos } = await verificarPagosAbiertos();
        
        let message = `<div class="space-y-3">
            <p>¿Estás seguro de que deseas dar de baja al cliente <strong>${client.nombre}</strong>?</p>`;
        
        // Agregar información de pagos abiertos si existen
        if (tienesPagosAbiertos) {
            const mesesFormateados = formatPendingMonths(pagosAbiertos);
            
            message += `
                <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                        </svg>
                        <strong class="text-yellow-800">¡Pagos Pendientes!</strong>
                    </div>
                    <p class="text-yellow-700 mt-1">
                        Este cliente tiene <strong>${cantidadPagosAbiertos} pago(s) pendiente(s) de cobro</strong> correspondientes a:
                    </p>
                    <p class="text-yellow-700 font-medium">
                        ${mesesFormateados}
                    </p>
                    <p class="text-yellow-700 text-sm">
                        Estos pagos serán eliminados permanentemente.
                    </p>
                </div>`;
        }
        
        // Agregar información del contrato si existe
        if (activeContract) {
            const estadoContrato = activeContract.estado === 'firmado' ? '✅ Firmado' 
                : activeContract.estado === 'parcialmente_firmado' ? '⏳ Parcialmente firmado'
                : activeContract.estado === 'pendiente' ? '⏳ Pendiente de firma'
                : '📄 ' + activeContract.estado;
                
            const firmasInfo = activeContract.firmas && activeContract.firmasRequeridas 
                ? `(${activeContract.firmas.length}/${activeContract.firmasRequeridas} firmas)`
                : '';
            
            message += `
                <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"></path>
                        </svg>
                        <strong class="text-blue-800">Contrato Digital</strong>
                    </div>
                    <div class="mt-2 text-sm text-blue-900">
                        <p><strong>Estado:</strong> ${estadoContrato} ${firmasInfo}</p>
                        <p><strong>Firmante:</strong> ${activeContract.firmanteNombre}</p>
                        <p><strong>Tipo:</strong> ${activeContract.tipoFirmante}</p>
                    </div>
                    <div class="mt-2 bg-blue-100 rounded p-2">
                        <p class="text-sm text-blue-900">
                            <strong>🗄️ El contrato será archivado automáticamente.</strong>
                        </p>
                        <p class="text-xs text-blue-700 mt-1">
                            ℹ️ El contrato no se elimina, solo se archiva. Podrás reactivarlo si restauras al cliente.
                        </p>
                    </div>
                </div>`;
        }
        
        message += `</div>`;
        
        setConfirmMessage(message);
        setShowDeleteConfirmation(true);
    };

    // Función para manejar la eliminación
    const handleDeleteClient = async () => {
        if (!clientId) return;
        
        try {
            // Si el cliente tiene más de una suscripción, vamos a eliminar todas
            const subscriptionIds: string[] = client.suscripciones?.map((s: Suscripcion) => s._id) || [];
            
            // Si solo tiene una suscripción, la pasamos directamente
            const subscriptionId = subscriptionIds.length === 1 
                ? subscriptionIds[0] 
                : undefined; // Si hay más de una o ninguna, dejamos que el back las maneje
            
            // Verificar si es cliente de otros pagos para incluir parámetro de eliminación de pagos
            const esClienteOtrosPagos = client.suscripciones?.some((s: Suscripcion) => 
                s.nombreSuscripcion === "pilates-otros-pagos"
            );

            const response = await deleteClientMutation.mutateAsync({
                clientId,
                subscriptionId,
                deleteOpenPayments: esClienteOtrosPagos
            });
            
            // Mostrar mensaje adicional si se archivó el contrato
            if (response?.contratoArchivado) {
                toast.success('✅ Cliente dado de baja y contrato archivado correctamente', {
                    autoClose: 4000
                });
            }
            
            // No se redirige: el cliente sigue existiendo, solo pasa a inactivo,
            // y la ficha ya lo refleja con la etiqueta "Inactivo". Antes esto
            // hacía `navigate('/clientes')` —sin tenantCode—, que caía en
            // `/:tenantCode/*` leyendo "clientes" como tenant y dejaba la sesión
            // apuntando al tenant equivocado (403 en cadena).
        } catch (error) {
            console.error('Error al eliminar cliente:', error);
            // El toast de error lo maneja el hook
        } finally {
            setShowDeleteConfirmation(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }
    
    if (error || !client) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                Error al cargar los datos del cliente - {error?.message}
            </div>
        );
    }
    
    // Encontrar la suscripción en edición
    const suscripcionActual = client.suscripciones?.find((s: Suscripcion) => s._id === suscripcionEnEdicion);
    
    return (
        <div className="container mx-auto px-4 py-8 pb-16"> {/* Agregamos padding bottom extra */}
            <div className="flex items-center mb-6">
                <Link to={`/${tenantCode}/clientes`} className="mr-4 flex items-center text-blue-600 hover:text-blue-800">
                    <ArrowLeft size={16} className="mr-1" />
                    Volver al listado
                </Link>
                <h1 className="text-2xl font-bold">{client.nombre}</h1>
                {!client.activo && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                        Inactivo
                    </span>
                )}
                {client.enEspera && (
                    <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs uppercase">
                        Cliente con plazas en espera 
                        - <strong>Desde</strong> {new Date(client.fechaInicioEspera).toLocaleDateString()} 
                        - <strong>Hasta</strong> {new Date(client.fechaFinEspera).toLocaleDateString()} 
                        - <strong>Motivo:</strong> {client.motivoEspera}
                    </span>
                )}
            </div>

            {/* Estado de la cuenta de portal del cliente */}
            {clientId && (
                <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <PortalAccountStatus clientId={clientId} />
                </div>
            )}

            {/* Mensaje de aviso para clientes SIN PLAZAS */}
            {client.activo && (!client.plazas || client.plazas.length === 0) && (
                <div className="mb-6 bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 rounded shadow-md">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <AlertTriangle size={20} className="mr-2" />
                            <div>
                                <p className="font-bold">Cliente sin plazas asignadas</p>
                                <p className="mt-1">
                                    Este cliente está activo pero no tiene ninguna plaza reservada. 
                                    Es necesario asignarle una plaza para completar su inscripción.
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setShowGestionarPlazas(true)}
                            className="ml-4 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 font-medium whitespace-nowrap"
                        >
                            Asignar Plaza
                        </button>
                    </div>
                </div>
            )}

            {/* Mensaje de aviso para clientes inactivos */}
            {!client.activo && (
                <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                    <div className="flex items-center">
                        <AlertTriangle size={20} className="mr-2" />
                        <p className="font-bold">Cliente Inactivo</p>
                    </div>
                    <p className="mt-2">
                        Para poder editar este cliente se debe primero restaurar desde la sección "Clientes de Baja". 
                        Los clientes inactivos no pueden ser modificados ni gestionar sus plazas o suscripciones.
                    </p>
                </div>
            )}
            
            {/* Botón para eliminar cliente - solo se muestra si el cliente está activo */}
            {client.activo && (
                <div className='flex justify-end mb-4'>
                    <button 
                        onClick={handleDeleteButtonClick}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded flex items-center"
                        title="Eliminar cliente"
                    >
                        <Trash2 size={16} className="mr-1" />
                        Dar de baja
                    </button>
                </div>
            )}
            

            {/* Información del cliente */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Datos personales */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4 border-b pb-2">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold">Datos Personales</h2>
                            {client.fechaNacimiento && esMenorDeEdad(client.fechaNacimiento) && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">
                                    ⚠️ Menor de edad
                                </span>
                            )}
                        </div>
                        <button 
                            onClick={() => setShowEditarDatos(true)}
                            className={`${client.activo ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                            title={client.activo ? "Editar datos personales" : "No se pueden editar datos de clientes inactivos"}
                            disabled={!client.activo}
                        >
                            <Edit size={16} />
                        </button>
                    </div>
                    <div className="space-y-2">
                        <p><strong>DNI:</strong> {client.dni}</p>
                        <p><strong>Email:</strong> {client.email || '-'}</p>
                        <p><strong>Teléfono:</strong> {client.telefono}</p>
                        {client.fechaNacimiento ? (
                            <p>
                                <strong>Fecha de Nacimiento:</strong> {formatearFecha(client.fechaNacimiento)} 
                                <span className="ml-2 text-gray-600">({calcularEdad(client.fechaNacimiento)} años)</span>
                            </p>
                        ) : (
                            <p className="text-yellow-600">
                                <strong>Fecha de Nacimiento:</strong> No registrada ⚠️
                            </p>
                        )}
                        <p><strong>Fecha de alta:</strong> {new Date(client.fechaAlta).toLocaleDateString()}</p>
                        {client.observaciones && (
                            <p><strong>Referencias:</strong> {client.observaciones}</p>
                        )}
                        {/* Información administrativa */}
                        <div className="mt-4 space-y-2 border-t border-gray-100">
                            <p><strong>Registrado por:</strong> {client.createdBy || '-'}</p>
                            <p><strong>Última modificación:</strong> {new Date(client.fechaModificacion || client.updatedAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>
                
                {/* Plazas */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4 border-b pb-2">
                        <h2 className="text-lg font-bold">Plazas Reservadas</h2>
                        <button 
                            onClick={() => setShowGestionarPlazas(true)}
                            className={`${client.activo ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                            title={client.activo ? "Gestionar plazas" : "No se pueden gestionar plazas de clientes inactivos"}
                            disabled={!client.activo}
                        >
                            <Edit size={16} />
                        </button>
                    </div>
                    {client.plazas && client.plazas.length > 0 ? (
                        <div className="space-y-2">
                            {ordenarPorDiaSemana(client.plazas).map((plaza: Plaza) => (
                                <div key={plaza._id} className="border-b pb-2">
                                    <p><strong>Día:</strong> {plaza.dia}</p>
                                    <p><strong>Hora:</strong> {plaza.horaInicio}</p>
                                    <p><strong>Nivel:</strong> {typeof plaza.nivel_id === 'object' ? plaza.nivel_id.nombre : 'Desconocido'}</p>
                                    {plaza.enEspera && (
                                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                            En espera
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">No tiene plazas reservadas</p>
                    )}
                    
                </div>
                
                {/* Suscripciones */}
                <div className="bg-white p-6 rounded-lg shadow">
                    {client.suscripciones && client.suscripciones.length > 0 ? (
                        <div className="space-y-4">
                            {client.suscripciones.map((suscripcion: Suscripcion) => (
                                <div key={suscripcion._id}>
                                    <div  className="border-b pb-4">
                                        <div className="flex justify-between items-start mb-2 border-b pb-2">
                                            <h2 className="text-lg font-bold">Suscripción: {etiquetaSuscripcion(suscripcion.nombreSuscripcion)}</h2>
                                            <button 
                                                onClick={() => setSuscripcionEnEdicion(suscripcion._id)}
                                                className={`${client.activo ? 'text-blue-600 hover:text-blue-800' : 'text-gray-400 cursor-not-allowed'}`}
                                                title={client.activo ? "Editar suscripción" : "No se pueden editar suscripciones de clientes inactivos"}
                                                disabled={!client.activo}
                                            >
                                                <Edit size={16} />
                                            </button>
                                        </div>
                                        <p>
                                            <strong>Importe:</strong> {formatNumber(suscripcion.importeSuscripcion)}€
                                            {suscripcion.precioFijo && (
                                                <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800" title="Puesto a mano por un admin: no cambia con las plazas ni con las tarifas">
                                                    Precio fijo
                                                </span>
                                            )}
                                        </p>
                                        {suscripcion.desglose && suscripcion.desglose.length > 0 && (
                                            <div className="pl-3 my-1 border-l-2 border-gray-200">
                                                <DesglosePrecio desglose={suscripcion.desglose} />
                                            </div>
                                        )}
                                        <p>
                                            <strong>Descuento: </strong>
                                            {typeof suscripcion.descuento === 'number'
                                            ?  formatNumber(suscripcion.descuento)
                                            : " 0,00"}€
                                        </p>
                                        {/* Sin descuento guardado la resta daba NaN */}
                                        <strong><p className='border-b pb-2'>Total Importe: {formatNumber(suscripcion.importeSuscripcion - (suscripcion.descuento || 0))}€</p></strong>
                                        <p className='pt-2'><strong>Frecuencia:</strong> {suscripcion.frecuencia}</p>
                                        <p><strong>Tipo de pago:</strong> {suscripcion.tipoPago || '-'}</p>
                                        
                                        {/* Caso 1: Domiciliaciones - mostrar IBAN */}
                                        {suscripcion.tipoPago === 'Domiciliaciones' && (
                                            <div className="mt-2">
                                                <div className="flex items-center">
                                                    <strong>IBAN:</strong>
                                                    {suscripcion.iban && suscripcion.iban.trim() !== '' ? (
                                                        <span className="ml-1">{suscripcion.iban}</span>
                                                    ) : (
                                                        <div className="ml-1 bg-red-100 text-red-700 px-2 py-1 rounded-md flex items-center">
                                                            <AlertTriangle size={16} className="mr-1" />
                                                            <span className="font-medium">¡No configurado!</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {(!suscripcion.iban || suscripcion.iban.trim() === '') && (
                                                    <div className="text-sm text-red-600 mt-1 p-2 bg-red-50 rounded border border-red-200">
                                                        <p className="flex items-center">
                                                            <AlertTriangle size={14} className="mr-1" />
                                                            Esta suscripción requiere un IBAN para las domiciliaciones.
                                                        </p>
                                                        <p className="mt-1 ml-5 text-xs">
                                                            Edite la suscripción para añadir los datos bancarios del cliente.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Estado y fechas de la suscripción */}
                                        <div className="mt-4 space-y-2 border-t border-gray-100">
                                            <div className="flex items-center">
                                                <p><strong>Estado:</strong></p>
                                                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${suscripcion.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {suscripcion.activo ? 'Activa' : 'Inactiva'}
                                                </span>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-2 mt-1">
                                                <p><strong>Fecha alta:</strong> {new Date(suscripcion.fechaAltaContrato).toLocaleDateString()}</p>
                                                {!suscripcion.activo && suscripcion.fechaBajaContrato && (
                                                    <p><strong>Fecha baja:</strong> {new Date(suscripcion.fechaBajaContrato).toLocaleDateString()}</p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Información administrativa */}
                                        <div className="mt-4 space-y-2 border-t border-gray-100">
                                            <p><strong>Registrado por:</strong> {suscripcion.createdBy || '-'}</p>
                                            <p><strong>Fecha de registro:</strong> {new Date(suscripcion.fechaCreacion).toLocaleDateString()}</p>
                                            <p><strong>Última modificación:</strong> {suscripcion.updatedAt ? new Date(suscripcion.updatedAt).toLocaleDateString() : '-'}</p>
                                        </div>

                                        {/* Mostrar información del beneficiario si existe */}
                                        {isMounted.current && suscripcion.beneficiario ? (
                                            <div key={`beneficiario-${suscripcion.beneficiario}`}>
                                                <BeneficiarioInfo beneficiarioId={suscripcion.beneficiario} />
                                            </div>
                                        ) : null}
                                        
                                        {/* Caso 2: Pagador - No Cliente */}
                                        {suscripcion.tipoPago === 'Pagador - No Cliente' && suscripcion.pagador && (
                                            <div className="mt-2 p-3 bg-blue-50 rounded-md">
                                                <h4 className="font-semibold text-blue-800 mb-1">Datos del Pagador</h4>
                                                <p><strong>Nombre:</strong> {suscripcion.pagador.nombre}</p>
                                                <p><strong>DNI:</strong> {suscripcion.pagador.DNI}</p>
                                                {suscripcion.pagador.IBAN && (
                                                    <p><strong>IBAN:</strong> {suscripcion.pagador.IBAN}</p>
                                                )}
                                                <p><strong>Teléfono:</strong> {suscripcion.pagador.telefono}</p>
                                                {suscripcion.pagador.email && (
                                                    <p><strong>Email:</strong> {suscripcion.pagador.email}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Caso 3: Pagador - Cliente */}
                                        {isMounted.current && suscripcion.tipoPago === 'Pagador - Cliente' && suscripcion.pagador_id ? (
                                            <div key={`pagador-${suscripcion.pagador_id}`}>
                                                <PagadorSuscripcionInfo pagadorSuscripcionId={suscripcion.pagador_id} />
                                            </div>
                                        ) : null}
                                        {suscripcion.observaciones && <p><strong>Observaciones:</strong> {suscripcion.observaciones}</p>}

                                        {/* {!suscripcion.activo && (
                                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                                Inactiva
                                            </span>
                                        )} */}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">No tiene suscripciones activas</p>
                    )}
                </div>
            </div>
            
            {enEspera.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-1">En lista de espera</h2>
                    <p className="text-xs text-gray-500 mb-3">
                        Clases completas para las que está apuntado. Al quedar sitio entra quien lleve
                        más tiempo esperando y tenga una recuperación válida.
                    </p>
                    <ul className="space-y-2">
                        {enEspera.map((entrada) => (
                            <li
                                key={entrada._id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2"
                            >
                                <div className="text-sm text-amber-900">
                                    <strong>{entrada.eventId?.nivel_id?.nombre ?? 'Clase'}</strong>
                                    {entrada.eventId && (
                                        <span>
                                            {' · '}{new Date(entrada.eventId.fecha).toLocaleDateString()}
                                            {' · '}{entrada.eventId.horaInicio}–{entrada.eventId.horaFin}
                                            {entrada.eventId.sala ? ` · Sala ${entrada.eventId.sala}` : ''}
                                        </span>
                                    )}
                                </div>
                                <span className="text-[11px] text-gray-500">
                                    Se apuntó el {new Date(entrada.createdAt).toLocaleDateString()}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Sección de pagos para clientes de "pilates-otros-pagos" - Componente refactorizado */}
            <PagosClienteSection 
                clientId={clientId || ''}
                suscripcionOtrosPagos={suscripcionOtrosPagos}
                clienteActivo={client.activo}
            />
            
            {/* Sección de contratos — desactivada hasta que la función esté lista.
                Para reactivarla, volver a poner <ClientContractSection> con las
                props comentadas debajo; el componente sigue intacto. */}
            <ContractsComingSoon />
            {/* <ClientContractSection
                clientId={client._id}
                clientName={client.nombre}
                isClientActive={client.activo}
                subscriptions={client.suscripciones || []}
                clientEmail={client.email}
            /> */}
            
            {/* Sección de eventos colapsable */}
            <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <button 
                    onClick={() => setShowEvents(!showEvents)}
                    className="w-full flex justify-between items-center"
                >
                    <div className="flex items-center">
                        <Calendar className="mr-2" size={20} />
                        <h2 className="text-lg font-bold">Próximos eventos</h2>
                    </div>
                    {showEvents ? <ChevronUp /> : <ChevronDown />}
                </button>
                
                {showEvents && (
                    <>
                        {eventsLoading ? (
                            <div className="flex justify-center my-4">
                                <Loader2 className="animate-spin" />
                            </div>
                        ) : eventos && eventos.length > 0 ? (
                            <div className="mt-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {eventos.map(evento => (
                                        <div key={evento._id} className="border rounded-lg p-4 hover:bg-gray-50">
                                            <div className="font-bold">
                                                {format(new Date(evento.fecha), 'EEEE d MMMM', { locale: es })}
                                            </div>
                                            <div>{evento.horaInicio} - {evento.horaFin}</div>
                                            <div className="text-sm">
                                                <span className="font-medium">{evento.nivel?.nombre ?? 'Nivel eliminado'}</span>
                                                {" "} con {evento.instructor?.nombre ?? 'instructor eliminado'}
                                            </div>
                                            <div className="text-xs text-gray-500">{evento.sala}</div>
                                            {/* Cancelar en nombre del cliente (sin correo o que llama):
                                                mismas reglas que su portal. */}
                                            {eventoACancelar === evento._id ? (
                                                <div className="mt-2 text-xs bg-amber-50 border border-amber-300 rounded p-2">
                                                    <p>Se le saca de esta clase. Gana recuperación si avisa dentro del plazo del centro.</p>
                                                    <div className="flex gap-2 mt-2">
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await cancelarPorCliente({ eventId: evento._id, clientId: clientId! });
                                                                } finally {
                                                                    setEventoACancelar(null);
                                                                }
                                                            }}
                                                            disabled={cancelandoPorCliente}
                                                            className={`px-2 py-1 rounded text-white ${cancelandoPorCliente ? 'bg-gray-400' : 'bg-amber-600'}`}
                                                        >
                                                            Cancelar su clase
                                                        </button>
                                                        <button
                                                            onClick={() => setEventoACancelar(null)}
                                                            className="px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Volver
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setEventoACancelar(evento._id)}
                                                    className="mt-2 text-xs text-amber-700 hover:text-amber-900 underline"
                                                >
                                                    No viene a esta clase
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                
                                {eventos.length === eventLimit && (
                                    <button 
                                        onClick={() => setEventLimit(prev => prev + 5)}
                                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center mx-auto"
                                    >
                                        <ChevronDown size={16} className="mr-1" />
                                        Ver más eventos
                                    </button>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-500 my-4">No hay próximos eventos para este cliente</p>
                        )}
                    </>
                )}
            </div>

            {/* Clases por recuperar (M10) */}
            <ClientRecoveriesCard clientId={clientId} />

            {/* P6 — desde la colección propia, no desde el feed: ese caduca a 60 días. */}
            <ClientAttendanceCard clientId={clientId} />

            {/* Historial de actividad (G1) */}
            <ClientActivitySection clientId={clientId} />

            {/* Modales de edición */}
            {showEditarDatos && 
                <EditarDatosPersonales 
                    cliente={client} 
                    onClose={() => setShowEditarDatos(false)} 
                    clientId={clientId || ''}
                />
            }
            
            {suscripcionEnEdicion && suscripcionActual && 
                <EditarSuscripcion 
                    suscripcion={suscripcionActual} 
                    onClose={() => setSuscripcionEnEdicion(null)} 
                    clientId={clientId || ''}
                    // No pasamos onSuscripcionUpdated aquí porque ClienteDetalle usa queries ['client']
                    // que se invalidan automáticamente en EditarSuscripcion. Solo SepaClientsList
                    // necesita invalidar queries adicionales ['sepa-clients']
                />
            }

            {showGestionarPlazas && client && (
                <GestionarPlazasCliente
                    clientId={clientId || ''}
                    plazasActuales={client.plazas || []}
                    suscripcionId={client.suscripcion?._id || client.suscripciones?.[0]?._id || ''}
                    onClose={() => setShowGestionarPlazas(false)}
                />
            )}

            {/* Modal de confirmación para eliminar */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <div className="flex items-center text-red-600 mb-4">
                            <AlertTriangle size={24} className="mr-2" />
                            <h3 className="text-xl font-bold">Confirmar eliminación</h3>
                        </div>
                        
                        <div className="mb-4">
                            {parse(confirmMessage)}
                        </div>
                        
                        <div className="border-t border-gray-200 pt-4">
                            <p className="text-sm text-gray-600 mb-4">
                                Esta acción desactivará al cliente, sus suscripciones y plazas, lo sacará de los eventos futuros (liberando su sitio) y revocará su acceso al portal. El historial del cliente se conserva.
                            </p>

                            {client.plazas && client.plazas.length > 0 && (
                                <div className="bg-yellow-50 p-3 rounded-md mb-4">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Importante:</strong> Se desactivarán {client.plazas.length} plazas asociadas a este cliente.
                                    </p>
                                </div>
                            )}
                            
                            {client.suscripciones?.some((s: Suscripcion) => s.beneficiario) && (
                                <div className="bg-red-50 p-3 rounded-md mb-4">
                                    <p className="text-sm text-red-800">
                                        <strong>Advertencia:</strong> Este cliente tiene beneficiarios asociados. Es recomendable revisar esta información antes de proceder.
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setShowDeleteConfirmation(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteClient}
                                disabled={deleteClientMutation.isPending}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                            >
                                {deleteClientMutation.isPending ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin mr-2" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} className="mr-2" />
                                        Confirmar baja
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClienteDetalle;